import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import NavigationBar from "../components/NavigationBar";
import ConsultationSection from "../components/ConsultationSection";
import { getConsultationsByDate } from "../services/consultationform";
import { ConsultationsResponse } from "../types/consultation";

const ConsultationsPage = () => {
  const navigate = useNavigate();

  const [consultations, setConsultations] =
    useState<ConsultationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formattedDate = selectedDate.toISOString().split("T")[0];

  useEffect(() => {
    const loadConsultations = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getConsultationsByDate(formattedDate);
        setConsultations(data);
      } catch (err: any) {
        console.error("Consultations API error:", err);
        setError("Failed to fetch consultations. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadConsultations();
  }, [formattedDate]);

  const handleConsultationClick = (id: string | number) => {
    navigate(`/consultation-response/${id}`);
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /**
   * 🔁 Map backend consultation → UI-friendly object
   * Includes customerId + formId (needed for resend)
   */
  const mapConsultation = (item: any) => ({
    id: String(item.id),

    // UI fields
    clientName: item.user?.name || "Unknown Client",
    consultationType: item.form?.title || "Untitled Form",
    time: formatTime(item.created_at),

    services:
      item.form?.business_services && Array.isArray(item.form.business_services)
        ? item.form.business_services.map((s: any) => ({
            name: s.name,
            category: s.category?.name,
            color: s.category?.color_code,
          }))
        : [],

    email: item.user?.email,
    mobile: item.user?.mobile,
    landline: item.user?.landline,

    // 🔑 REQUIRED FOR RESEND (optional but present here)
    customerId: item.user_id ?? item.user?.id,
    formId: item.form_id ?? item.form?.id,
  });

  const incompleteConsultations =
    consultations?.incomplete_forms?.map(mapConsultation) || [];

  const completedConsultations =
    consultations?.completed_forms?.map(mapConsultation) || [];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-4xl mx-auto px-6 pt-8 space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Consultations
          </h1>
          <p className="text-gray-500 font-medium">
            Manage and view your client consultation responses
          </p>
        </div>

        {/* Date Navigation */}
        <NavigationBar
          currentDate={selectedDate}
          onDateChange={(newDate: Date) => setSelectedDate(newDate)}
        />

        {/* States */}
        {loading && (
          <div className="text-center text-gray-500 py-10">
            Loading consultations...
          </div>
        )}

        {error && <div className="text-center text-red-500 py-10">{error}</div>}

        {!loading && !error && (
          <div className="space-y-6">
            <ConsultationSection
              title="Incomplete Consultations"
              consultations={incompleteConsultations}
              onConsultationClick={handleConsultationClick}
            />

            <ConsultationSection
              title="Completed Consultations"
              consultations={completedConsultations}
              onConsultationClick={handleConsultationClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultationsPage;
